/**
 * Yard Location Scheme API Hooks.
 *
 * React Query hooks for managing location naming schemes and the
 * generated locations within yard sections.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type {
  LocationScheme,
  SchemePreview,
  SegmentDefinition,
  YardLocation,
} from "@/types/locations";

// ---------------------------------------------------------------------------
// Location Schemes
// ---------------------------------------------------------------------------

export function useLocationSchemes() {
  return useQuery<LocationScheme[]>({
    queryKey: ["location-schemes"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/location-schemes"))
        .data as LocationScheme[],
  });
}

export function useLocationScheme(schemeId: string | undefined) {
  return useQuery<LocationScheme>({
    queryKey: ["location-schemes", schemeId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/location-schemes/${schemeId}`))
        .data as LocationScheme,
    enabled: !!schemeId,
  });
}

export function useCreateLocationScheme() {
  const qc = useQueryClient();
  return useMutation<
    LocationScheme,
    Error,
    {
      name: string;
      description?: string;
      segments: SegmentDefinition[];
      separator?: string;
    }
  >({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/location-schemes", data))
        .data as LocationScheme,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["location-schemes"] }),
  });
}

export function useUpdateLocationScheme() {
  const qc = useQueryClient();
  return useMutation<
    LocationScheme,
    Error,
    {
      id: string;
      data: Partial<{
        name: string;
        description: string;
        segments: SegmentDefinition[];
        separator: string;
        is_active: boolean;
      }>;
    }
  >({
    mutationFn: async ({ id, data }) =>
      (await apiClient.put(`/api/v1/location-schemes/${id}`, data))
        .data as LocationScheme,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["location-schemes"] }),
  });
}

export function useDeleteLocationScheme() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/api/v1/location-schemes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["location-schemes"] }),
  });
}

export function usePreviewScheme(schemeId: string | undefined) {
  return useQuery<SchemePreview>({
    queryKey: ["location-schemes", schemeId, "preview"],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/location-schemes/${schemeId}/preview`))
        .data as SchemePreview,
    enabled: !!schemeId,
  });
}

// ---------------------------------------------------------------------------
// Storage Areas (independent of yard maps)
// ---------------------------------------------------------------------------

export interface StorageArea {
  id: string;
  business_location_id: string | null;
  yard_id: string | null;
  label: string;
  section_type: string;
  capacity: number | null;
  notes: string | null;
  location_scheme_id: string | null;
  location_capacity: number | null;
  location_count: number;
  asset_count: number;
  is_placed: boolean;
  computed_capacity: number | null;
  locations: Array<{
    id: string;
    label: string;
    sort_order: number;
    capacity: number;
    is_active: boolean;
  }>;
  created_at: string;
}

export function useStorageAreas(businessLocationId?: string | null) {
  return useQuery<StorageArea[]>({
    queryKey: ["storage-areas", businessLocationId ?? "all"],
    queryFn: async () => {
      const params = businessLocationId
        ? { business_location_id: businessLocationId }
        : {};
      return (await apiClient.get("/api/v1/storage-areas", { params }))
        .data as StorageArea[];
    },
  });
}

export function useCreateStorageArea() {
  const qc = useQueryClient();
  return useMutation<
    StorageArea,
    Error,
    {
      label: string;
      section_type?: string;
      business_location_id: string;
      location_scheme_id?: string;
      capacity?: number;
      notes?: string;
      location_capacity?: number;
    }
  >({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/storage-areas", data)).data as StorageArea,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["storage-areas"] }),
  });
}

export function useUpdateStorageArea() {
  const qc = useQueryClient();
  return useMutation<
    StorageArea,
    Error,
    {
      id: string;
      data: Partial<{
        label: string;
        section_type: string;
        capacity: number;
        notes: string;
        location_scheme_id: string;
        location_capacity: number;
      }>;
    }
  >({
    mutationFn: async ({ id, data }) =>
      (await apiClient.put(`/api/v1/storage-areas/${id}`, data))
        .data as StorageArea,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["storage-areas"] }),
  });
}

export function useDeleteStorageArea() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/api/v1/storage-areas/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["storage-areas"] }),
  });
}

// ---------------------------------------------------------------------------
// Reference features (buildings, fences, driveways, gates, landmarks)
// ---------------------------------------------------------------------------

export interface FeatureInput {
  label: string;
  kind: string;
  geometry_type: "polygon" | "polyline";
  points: Array<{ x: number; y: number }>;
  color?: string | null;
  notes?: string | null;
}

/** Create a reference feature on a facility map. */
export function useCreateFeature(yardId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, FeatureInput>({
    mutationFn: async (data) =>
      (await apiClient.post(`/api/v1/yards/${yardId}/features`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yard-map", yardId] }),
  });
}

/** Update a reference feature (label, kind, geometry, points, color). */
export function useUpdateFeature(yardId: string) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { featureId: string; data: Partial<FeatureInput> }
  >({
    mutationFn: async ({ featureId, data }) =>
      (
        await apiClient.put(
          `/api/v1/yards/${yardId}/features/${featureId}`,
          data,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yard-map", yardId] }),
  });
}

/** Delete a reference feature. */
export function useDeleteFeature(yardId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (featureId) => {
      await apiClient.delete(`/api/v1/yards/${yardId}/features/${featureId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yard-map", yardId] }),
  });
}

/**
 * Place an existing (unplaced) storage area onto a facility map, giving it a
 * position and size. Mirrors the backend `PlaceAreaOnMapRequest`. Invalidates
 * both the facility-map render query and the storage-area list so the area
 * moves out of the "unplaced" palette.
 */
export function usePlaceArea() {
  const qc = useQueryClient();
  return useMutation<
    StorageArea,
    Error,
    {
      areaId: string;
      yard_id: string;
      x?: number;
      y?: number;
      width?: number;
      height?: number;
      color?: string;
      shape_type?: string;
      rotation?: number;
      points?: Array<{ x: number; y: number }> | null;
    }
  >({
    mutationFn: async ({ areaId, ...body }) =>
      (await apiClient.post(`/api/v1/storage-areas/${areaId}/place`, body))
        .data as StorageArea,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["yard-map", vars.yard_id] });
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Section Locations
// ---------------------------------------------------------------------------

export function useSectionLocations(yardId: string, sectionId: string | null) {
  return useQuery<YardLocation[]>({
    queryKey: ["yard-locations", yardId, sectionId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/yards/${yardId}/sections/${sectionId}/locations`,
          { params: { page_size: 100 } },
        )
      ).data.items as YardLocation[],
    enabled: !!sectionId,
  });
}

export function useApplyScheme(yardId: string) {
  const qc = useQueryClient();
  return useMutation<
    { location_count: number },
    Error,
    {
      sectionId: string;
      scheme_id: string;
      location_capacity?: number;
    }
  >({
    mutationFn: async ({ sectionId, ...body }) =>
      (
        await apiClient.post(
          `/api/v1/yards/${yardId}/sections/${sectionId}/apply-scheme`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["yard-map", yardId] });
      qc.invalidateQueries({ queryKey: ["yard-locations"] });
    },
  });
}

export function useRemoveScheme(yardId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (sectionId) => {
      await apiClient.delete(
        `/api/v1/yards/${yardId}/sections/${sectionId}/locations`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["yard-map", yardId] });
      qc.invalidateQueries({ queryKey: ["yard-locations"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Storage Area Locations (manual CRUD via /storage-areas/{id}/locations)
// ---------------------------------------------------------------------------

export interface StorageAreaLocation {
  id: string;
  yard_section_id: string;
  label: string;
  sort_order: number;
  capacity: number;
  is_active: boolean;
  asset_count: number;
  created_at: string;
}

export interface PaginatedLocations {
  items: StorageAreaLocation[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export function useStorageAreaLocations(areaId: string | null) {
  return useQuery<PaginatedLocations>({
    queryKey: ["storage-area-locations", areaId],
    queryFn: async () =>
      (
        await apiClient.get(`/api/v1/storage-areas/${areaId}/locations`, {
          params: { page_size: 100 },
        })
      ).data as PaginatedLocations,
    enabled: !!areaId,
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation<
    StorageAreaLocation,
    Error,
    { areaId: string; label: string; capacity?: number }
  >({
    mutationFn: async ({ areaId, ...body }) =>
      (await apiClient.post(`/api/v1/storage-areas/${areaId}/locations`, body))
        .data as StorageAreaLocation,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["storage-area-locations", vars.areaId],
      });
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
    },
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation<
    StorageAreaLocation,
    Error,
    {
      areaId: string;
      locationId: string;
      data: Partial<{ label: string; capacity: number; is_active: boolean }>;
    }
  >({
    mutationFn: async ({ areaId, locationId, data }) =>
      (
        await apiClient.put(
          `/api/v1/storage-areas/${areaId}/locations/${locationId}`,
          data,
        )
      ).data as StorageAreaLocation,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["storage-area-locations", vars.areaId],
      });
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
    },
  });
}

export function useDeleteLocation() {
  const qc = useQueryClient();
  return useMutation<void, Error, { areaId: string; locationId: string }>({
    mutationFn: async ({ areaId, locationId }) => {
      await apiClient.delete(
        `/api/v1/storage-areas/${areaId}/locations/${locationId}`,
      );
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["storage-area-locations", vars.areaId],
      });
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Scheme Apply / Remove (via /storage-areas/{id})
// ---------------------------------------------------------------------------

export function useAssignSchemeToArea() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; location_scheme_id: string; detail: string },
    Error,
    { areaId: string; scheme_id: string }
  >({
    mutationFn: async ({ areaId, ...body }) =>
      (
        await apiClient.post(
          `/api/v1/storage-areas/${areaId}/assign-scheme`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
    },
  });
}

export function useApplySchemeToArea() {
  const qc = useQueryClient();
  return useMutation<
    { location_count: number },
    Error,
    {
      areaId: string;
      scheme_id: string;
      location_capacity?: number;
      segment_overrides?: Array<{
        index: number;
        start?: number | string;
        end?: number | string;
      }>;
    }
  >({
    mutationFn: async ({ areaId, ...body }) =>
      (
        await apiClient.post(
          `/api/v1/storage-areas/${areaId}/apply-scheme`,
          body,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
      qc.invalidateQueries({ queryKey: ["storage-area-locations"] });
    },
  });
}

export function useRemoveSchemeFromArea() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (areaId) => {
      await apiClient.post(`/api/v1/storage-areas/${areaId}/remove-scheme`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["storage-areas"] });
      qc.invalidateQueries({ queryKey: ["storage-area-locations"] });
    },
  });
}
